/**
 * Lighter TypeScript SDK
 * 
 * A comprehensive TypeScript SDK for interacting with the Lighter Protocol,
 * a decentralized perpetual exchange built on zkProof.
 */

// Core API Classes
export { ApiClient } from './api/api-client';
export { AccountApi } from './api/account-api';
export { AnnouncementApi } from './api/announcement-api';
export { BlockApi } from './api/block-api';
export { BridgeApi } from './api/bridge-api';
export { FundingApi } from './api/funding-api';
export { InfoApi } from './api/info-api';
export { NotificationApi } from './api/notification-api';
export { OrderApi } from './api/order-api';
export { ReferralApi } from './api/referral-api';
export { TransactionApi } from './api/transaction-api';
export { RootApi } from './api/root-api';
export { CandlestickApi } from './api/candlestick-api';
export { TokenlistApi } from './api/tokenlist-api';

// Explorer API Classes
export { ExplorerApiClient } from './api/explorer-api-client';
export type { ExplorerConfiguration } from './api/explorer-api-client';
export { SearchApi } from './api/search-api';
export { LogsApi } from './api/logs-api';

// Bridge Classes
export { L1BridgeClient } from './bridge/l1-bridge-client';

// Network registry + env-driven network selection (mainnet | testnet | robinhood)
export { getNetwork, resolveNetworkFromEnv, resolveWsUrl, deriveWsUrl, NETWORKS } from './network';
export type { Network, NetworkName } from './network';

// Signer Client
export { SignerClient } from './signer/wasm-signer-client';
export type { 
  SignerConfig,
  CreateOrderParams as SignerCreateOrderParams,
  CreateMarketOrderParams,
  CancelOrderParams as SignerCancelOrderParams,
  ChangeApiKeyParams,
  OcoOrderLegParams,
  OcoOrderParams,
  OtocoMainOrderParams,
  OtocoProtectionOrderParams,
  OtocoOrderParams
} from './signer/wasm-signer-client';

// Note: Signer Server Client removed - using local WASM signer instead

// WASM Signer Classes
export { WasmSignerClient, createWasmSignerClient, WasmManager } from './signer/wasm-signer';
export type { 
  WasmSignerConfig, 
  WasmConfig,
  WasmClientType,
  CreateClientParams,
  CreateOrderParams,
  CancelOrderParams,
  CancelAllOrdersParams,
  UpdateLeverageParams,
  ModifyOrderParams,
  UpdateMarginParams,
  CreateSubAccountParams,
  CreatePublicPoolParams,
  UpdatePublicPoolParams,
  MintSharesParams,
  BurnSharesParams,
  StakeAssetsParams,
  UnstakeAssetsParams,
  ApproveIntegratorParams,
  UpdateAccountConfigParams,
  UpdateAccountAssetConfigParams,
  CreateGroupedOrderParams,
  CreateGroupedOrdersParams,
  WasmSignerResponse,
  ApiKeyPair
} from './signer/wasm-signer';

// Export Order Types and Enums from the unified order file
export { 
  OrderType,
  GroupingType,
  TimeInForce,
  TransactionStatus,
  TransactionType
} from './signer/wasm-signer-client';

// Export MarketHelper for simplified market utilities
export { MarketHelper } from './utils/market-helper';

// Export Order Status Checking utilities
export { checkOrderStatus, formatOrderResult, getCancelReason } from './utils/order-status-checker';
export type { OrderStatusResult } from './utils/order-status-checker';

// WebSocket Client
export { WsClient } from './api/ws-client';
export type {
  AccountAllSubscriptionParams,
  WsAccountAllPosition,
  WsAccountAllMessage
} from './api/ws-client';
export { WebSocketOrderClient } from './api/ws-order-client';
export type {
  LighterWsSendTx,
  LighterWsSendBatchTx,
  LighterWsTransaction,
  WsOrderRequest,
  WsOrderResponse,
  WsConnectionConfig
} from './api/ws-order-client';

// Exception Classes
export {
  LighterException,
  ApiException,
  BadRequestException,
  UnauthorizedException,
  ForbiddenException,
  NotFoundException,
  TooManyRequestsException,
  ServiceException,
  ValidationException,
  ConfigurationException
} from './utils/exceptions';

// Types from AccountApi
export type { 
  Account,
  AccountAsset,
  SubAccount,
  AccountPosition,
  AccountApiKeys,
  ApiKey,
  PublicPool,
  PublicPoolShare,
  FeeBucket,
  PnLEntry,
  PnLResponse,
  AccountLimits,
  AccountMetadata,
  L1Metadata,
  PublicPoolsMetadataResponse,
  ApiTokensResponse,
  CreateApiTokenParams,
  CreateApiTokenResponse,
  RevokeApiTokenResponse,
  Liquidation,
  LiquidationResponse,
  PositionFunding,
  PositionFundingResponse,
  Trade
} from './api/account-api';

// Types from OrderApi
export type {
  OrderBook,
  OrderBookDetail,
  OrderBookDetailsResponse,
  OrderBookDetailItem,
  OrderBookOrders,
  Order,
  ExchangeStats,
  PriceLevel,
  OBDetails,
  PerpsOrderBookDetail,
  SpotOrderBookDetail,
  Asset,
  AssetDetails
} from './api/order-api';

// Types from TransactionApi
export type {
  Transaction,
  Block,
  NextNonce,
  TxHash,
  TxHashes,
  DepositHistory,
  DepositHistoryItem,
  WithdrawHistory,
  WithdrawHistoryItem
} from './api/transaction-api';

// Types from BridgeApi
export type {
  FastBridgeInfo,
  BridgeSupportedNetwork,
  BridgesByL1AddressResponse,
  IsNextBridgeFastResponse,
  FastWithdrawResponse,
  FastWithdrawInfoResponse
} from './api/bridge-api';
export type {
  TransferParams,
  TransferSameMasterAccountParams,
  WithdrawParams,
  L1DepositParams,
  L1DepositResult,
  L1BridgeConfig
} from './types/api';

// Types from FundingApi
export type {
  FundingRate,
  FundingRates
} from './api/funding-api';

// Types from NotificationApi
export type {
  ResultCode,
  PushNotifSettingsResponse
} from './api/notification-api';

// Types from ReferralApi
export type {
  ReferralPointEntry,
  ReferralPoints,
  UserReferrals
} from './api/referral-api';

// Types from RootApi
export type {
  SystemStatus
} from './api/root-api';

// Types from AnnouncementApi
export type {
  Announcement,
  Announcements
} from './api/announcement-api';

// Types from BlockApi
export type {
  BlockQuery,
  BlocksQuery,
  CurrentHeightResponse
} from './api/block-api';

// Types from CandlestickApi
export type {
  CandlestickQuery,
  FundingQuery
} from './api/candlestick-api';

// Types from SearchApi (Explorer)
export type {
  SearchResultType,
  TransactionLogResult,
  TransactionLog,
  PublicData,
  TradePubdata,
  OrderPubdata,
  BatchResult,
  BatchInfo,
  BlockResult,
  BlockInfo,
  AccountResult,
  AccountInfo,
  SearchResult,
  SearchParams
} from './api/search-api';

// Types from LogsApi (Explorer)
export type {
  LogQueryParams,
  AccountLogsResponse
} from './api/logs-api';

export type {
  OrderBookParams,
  TradeParams,
  BlockParams,
  PaginationParams,
  Configuration,
  ApiResponse,
  ApiError,
  WebSocketConfig,
  WebSocketSubscription
} from './types';

// Utility Classes
export { Config } from './utils/configuration';
// API Key utilities removed - use WASM signer for key generation

// Environment Detection Utilities (for browser/Node.js/Next.js compatibility)
export {
  isBrowser,
  isNodeJS,
  isDeno,
  isWorker,
  isNextJS,
  isNextJSClient,
  isNextJSServer,
  isReactNative,
  detectEnvironment,
  getWebSocketConstructor,
  hasCryptoSupport,
  hasLocalStorageSupport,
  hasIndexedDBSupport,
  RuntimeEnvironment
} from './utils/environment';

// New Utility Exports
export * from './utils/price-utils';
export * from './utils/nonce-manager';
// Client Factory removed - use direct client creation instead

// Constants
export const LIGHTER_CONSTANTS = {
  // Order Types
  ORDER_TYPE_LIMIT: 0,
  ORDER_TYPE_MARKET: 1,
  ORDER_TYPE_STOP_LOSS: 2,
  ORDER_TYPE_STOP_LOSS_LIMIT: 3,
  ORDER_TYPE_TAKE_PROFIT: 4,
  ORDER_TYPE_TAKE_PROFIT_LIMIT: 5,
  ORDER_TYPE_TWAP: 6,
  
  // Time in Force
  ORDER_TIME_IN_FORCE_IMMEDIATE_OR_CANCEL: 0,
  ORDER_TIME_IN_FORCE_GOOD_TILL_TIME: 1,
  ORDER_TIME_IN_FORCE_FILL_OR_KILL: 2,
  ORDER_TIME_IN_FORCE_POST_ONLY: 2,
  
  // Cancel All Orders Time in Force
  CANCEL_ALL_TIF_IMMEDIATE: 0,
  CANCEL_ALL_TIF_SCHEDULED: 1,
  CANCEL_ALL_TIF_ABORT: 2,
  
  // Self-Trade Behavior
  SELF_TRADE_BEHAVIOR_EXPIRE_MAKER: 0,
  SELF_TRADE_BEHAVIOR_EXPIRE_TAKER: 1,
  SELF_TRADE_BEHAVIOR_EXPIRE_BOTH: 2,
  SELF_TRADE_BEHAVIOR_REDUCE: 3,
  
  // Self-Trade Equality
  SELF_TRADE_EQUALITY_ACCOUNT_INDEX: 0,
  SELF_TRADE_EQUALITY_MASTER_ACCOUNT_INDEX: 1,
  
  // Asset Margin Mode
  ASSET_MARGIN_MODE_DISABLED: 0,
  ASSET_MARGIN_MODE_ENABLED: 1,
  
  // Margin Modes
  CROSS_MARGIN_MODE: 0,
  ISOLATED_MARGIN_MODE: 1,
  ISOLATED_MARGIN_REMOVE_COLLATERAL: 0,
  ISOLATED_MARGIN_ADD_COLLATERAL: 1,
  
  // Transaction Types
  TX_TYPE_CHANGE_PUB_KEY: 8,
  TX_TYPE_CREATE_SUB_ACCOUNT: 9,
  TX_TYPE_CREATE_PUBLIC_POOL: 10,
  TX_TYPE_UPDATE_PUBLIC_POOL: 11,
  TX_TYPE_TRANSFER: 12,
  TX_TYPE_WITHDRAW: 13,
  TX_TYPE_CREATE_ORDER: 14,
  TX_TYPE_CANCEL_ORDER: 15,
  TX_TYPE_CANCEL_ALL_ORDERS: 16,
  TX_TYPE_MODIFY_ORDER: 17,
  TX_TYPE_MINT_SHARES: 18,
  TX_TYPE_BURN_SHARES: 19,
  TX_TYPE_UPDATE_LEVERAGE: 20,
  TX_TYPE_CREATE_GROUPED_ORDERS: 28,
  TX_TYPE_UPDATE_MARGIN: 29,
  TX_TYPE_STAKE_ASSETS: 35,
  TX_TYPE_UNSTAKE_ASSETS: 36,
  TX_TYPE_APPROVE_INTEGRATOR: 45,
  TX_TYPE_UPDATE_ACCOUNT_CONFIG: 46,
  TX_TYPE_UPDATE_ACCOUNT_ASSET_CONFIG: 47,
  
  // Other Constants
  NIL_TRIGGER_PRICE: 0,
  NIL_MARKET_INDEX: 255,
  DEFAULT_28_DAY_ORDER_EXPIRY: -1,
  DEFAULT_IOC_EXPIRY: 0,
  DEFAULT_10_MIN_AUTH_EXPIRY: -1,
  MINUTE: 60,
  USDC_TICKER_SCALE: 1e6,
  
  // Transaction Status Codes
  TX_STATUS_PENDING: 0,
  TX_STATUS_QUEUED: 1,
  TX_STATUS_COMMITTED: 2,
  TX_STATUS_EXECUTED: 3,
  TX_STATUS_FAILED: 4,
  TX_STATUS_REJECTED: 5
} as const;

// Default Configuration
export const DEFAULT_CONFIG = {
  MAINNET_URL: 'https://mainnet.zklighter.elliot.ai',
  TESTNET_URL: 'https://testnet.zklighter.elliot.ai',
  DEFAULT_TIMEOUT: 30000,
  DEFAULT_RETRIES: 3
} as const;

// Version
export const VERSION = '1.0.13';